import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  MenuItem,
  SelectChangeEvent,
  Alert,
  Tabs,
  Tab,
  TextField,
} from '@mui/material';
import {
  apiErrorMessage,
  appointmentsService,
  invoicesService,
  mechanicsService,
  shopsService,
  tasksService,
} from '../../services/api';
import { shopWallClockDate, shopWallClockToEpoch } from '../../utils/shopTime';
import { useShopTimezones } from '../../hooks/useShopTimezones';
import { useCachedData } from '../../hooks/useCachedData';
import {
  findNextOpenDate,
  getSpecialHoursOn,
  isClosedDayOn,
  isDateOpen,
  isHolidayOn,
} from '../../utils/shopAvailability';
import {
  Appointment,
  AppointmentStatus,
  CreateAppointmentDto,
  Mechanic,
  Shop,
  ShopSettings,
  Task,
  TaskStatus,
  UpdateAppointmentDto,
  CreateTaskDto,
  UpdateTaskDto,
} from '../../types';
import { useSocket } from '../../contexts/SocketContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  format,
  addDays,
  startOfWeek,
  endOfWeek,
  isSameDay,
} from 'date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { BlockSkeleton, PageHeader } from '../../components/ui';
import {
  BUSINESS_HOURS,
  timezoneOptions,
  generateTimeSlots,
  DEFAULT_TIME_SLOTS,
  getAppointmentDate,
  HistoryWindow,
  HISTORY_WINDOWS,
  historyRange,
  formatTimeForDisplay,
} from './appointments/helpers';
import TabPanel from './appointments/TabPanel';
import AppointmentsTable from './appointments/AppointmentsTable';
import AppointmentCategories from './appointments/AppointmentCategories';
import TimeSlotsTab from './appointments/TimeSlotsTab';
import EditAppointmentDialog from './appointments/EditAppointmentDialog';
import RescheduleDialog from './appointments/RescheduleDialog';
import NewBookingDialog from './appointments/NewBookingDialog';
import TaskManagerDialog from './appointments/TaskManagerDialog';
import TaskFormDialog from './appointments/TaskFormDialog';

const NO_APPOINTMENTS: Appointment[] = [];
const NO_MECHANICS: Mechanic[] = [];
const NO_SHOPS: Shop[] = [];

const AppointmentsPage: React.FC = () => {
  /**
   * How far back to load. This page used to fetch every appointment a shop had
   * ever taken, each with its mechanic, user, shop and tasks eager-loaded — the
   * Today/Week/Categories views then filtered that in the browser. The window
   * is applied server-side instead, so those views still filter over everything
   * they need without the query being unbounded.
   */
  const [historyWindow, setHistoryWindow] = useState<HistoryWindow>('90d');
  // From the session cache first, then reloaded: returning here paints at
  // once. The default window is the calendar's too, so they share the entry.
  const appointmentsData = useCachedData(`appointments:${historyWindow}`, () =>
    appointmentsService.getAllAppointments(historyRange(historyWindow)),
  );
  const mechanicsData = useCachedData('mechanics', () => mechanicsService.getAllMechanics());
  const shopsData = useCachedData('shops', () => shopsService.getAllShops());
  const appointments = appointmentsData.data ?? NO_APPOINTMENTS;
  const mechanics = mechanicsData.data ?? NO_MECHANICS;
  const shops = shopsData.data ?? NO_SHOPS;
  const loading = appointmentsData.loading || mechanicsData.loading;
  const loadError = appointmentsData.error || mechanicsData.error;
  const { mutate: setAppointments, reload: refreshAppointments } = appointmentsData;
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [generatingInvoiceId, setGeneratingInvoiceId] = useState<string | null>(null);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  // The tab is the URL's ?view=slots, so a link there (the dashboard's "New
  // booking") switches to the time slots even while this page is open.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabValue = searchParams.get('view') === 'slots' ? 1 : 0;
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>(DEFAULT_TIME_SLOTS);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>('');
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState<boolean>(false);
  
  // State for shop selection in Available Time Slots view
  const [selectedShopForTimeSlots, setSelectedShopForTimeSlots] = useState<string>('');
  const [shopAppointments, setShopAppointments] = useState<Appointment[]>([]);
  const [shopBookedSlots, setShopBookedSlots] = useState<string[]>([]);
  const [isLoadingShopSlots, setIsLoadingShopSlots] = useState<boolean>(false);
  const [shopSettings, setShopSettings] = useState<ShopSettings | null>(null);
  const { shopId: ownShopId } = useAuth();

  // The shops a booking can be made for: the admin's own (even before it has
  // any bookings) and every shop in the loaded appointments (a super admin's).
  const timeSlotShopIds = useMemo(
    () =>
      Array.from(new Set([ownShopId, ...appointments.map((a) => a.shopId)])).filter(
        (id): id is string => Boolean(id),
      ),
    [ownShopId, appointments],
  );
  // With only one shop to choose from, choose it.
  useEffect(() => {
    if (!selectedShopForTimeSlots && timeSlotShopIds.length === 1) {
      setSelectedShopForTimeSlots(timeSlotShopIds[0]);
    }
  }, [selectedShopForTimeSlots, timeSlotShopIds]);

  // Appointment times are shop-local wall clock: read every one in its own
  // shop's zone, so the page is right for a viewer in any timezone.
  const tzOf = useShopTimezones([
    ...appointments.map((appointment) => appointment.shopId),
    ...shopAppointments.map((appointment) => appointment.shopId),
    selectedShopForTimeSlots,
  ]);
  const homeShopId = selectedShopForTimeSlots || appointments[0]?.shopId;
  /** "Now" on the shop's clock, for Today / This week. */
  const shopNow = useCallback(
    () => shopWallClockDate(Math.floor(Date.now() / 1000), tzOf(homeShopId)),
    [tzOf, homeShopId]
  );
  const [appointmentTasks, setAppointmentTasks] = useState<Task[]>([]);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [taskFormDialogOpen, setTaskFormDialogOpen] = useState(false);
  const [taskForm, setTaskForm] = useState<{ title: string; description: string; status: TaskStatus; assignedToId: string }>({
    title: '',
    description: '',
    status: TaskStatus.PENDING,
    assignedToId: '',
  });
  const [slotBookingCounts, setSlotBookingCounts] = useState<Record<string, number>>({});
  const [loadingSettings, setLoadingSettings] = useState<boolean>(false);
  
  // New state for new booking dialog
  const [newBookingDialogOpen, setNewBookingDialogOpen] = useState(false);
  const [newBookingTimeSlot, setNewBookingTimeSlot] = useState<string>('');
  const [newBookingError, setNewBookingError] = useState<string | null>(null);
  const [savingBooking, setSavingBooking] = useState(false);
  const [newBookingForm, setNewBookingForm] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    vehicleModel: '',
    vehicleYear: '',
    licensePlate: '',
    serviceDescription: '',
    comments: '',
    mechanicId: ''
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { appointments: socketAppointments, connected: socketConnected, error: socketError } = useSocket();

  // Add this state near the other states at the beginning of the component
  const [timeSlotError, setTimeSlotError] = useState<string | null>(null);
  const [appointmentListTab, setAppointmentListTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        if (newValue === 1) next.set('view', 'slots');
        else next.delete('view');
        return next;
      },
      { replace: true },
    );
    
    // When switching to time slots tab (index 1), check if current date is available
    if (newValue === 1 && shopSettings && !isDateAvailable(selectedDate)) {
      const nextAvailable = findNextAvailableDate(new Date());
      setSelectedDate(nextAvailable);
    }
  };

  const handleAppointmentListTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setAppointmentListTab(newValue);
  };

  // Update appointments when socket updates occur - with safety checks
  useEffect(() => {
    if (socketConnected && socketAppointments && socketAppointments.length > 0) {
      setAppointments(prev => {
        // Create a map of existing appointments for quicker lookup
        const existingAppointmentsMap = new Map(prev.map(a => [a.id, a]));
        
        // Process only new appointments
        const newAppointments = socketAppointments.filter(a => !existingAppointmentsMap.has(a.id));
        
        // If there are no new appointments, return the previous state
        if (newAppointments.length === 0) return prev;
        
        // Otherwise, add the new appointments
        return [...prev, ...newAppointments];
      });
    }
  }, [socketAppointments, socketConnected, setAppointments]);

  // Load available time slots for a given date
  useEffect(() => {
    const fetchAvailableTimeSlots = async () => {
      if (!selectedDate || !selectedAppointment?.shopId) return;

      try {
        setIsLoadingTimeSlots(true);
        const formattedDate = format(selectedDate, 'yyyy-MM-dd');

        // The slot list comes from the API, which already applies business
        // hours and the shop's concurrent-booking limit — the page used to
        // fetch the day's appointments to work that out a second time.
        try {
          // Try to get API-provided available slots
          const slots = await appointmentsService.getAvailableTimeSlots(selectedAppointment.shopId, formattedDate);
          setAvailableTimeSlots(slots);
        } catch (error) {
          console.error('Failed to get API time slots:', error);
          // Show all time slots but mark booked ones separately
          setAvailableTimeSlots(DEFAULT_TIME_SLOTS);
        }
      } catch (error) {
        console.error('Failed to load time slots:', error);
        // If all else fails, show all time slots
        setAvailableTimeSlots(DEFAULT_TIME_SLOTS);
      } finally {
        setIsLoadingTimeSlots(false);
      }
    };

    fetchAvailableTimeSlots();
  }, [selectedDate, selectedAppointment]);

  // Fetch booked slots for the selected shop and date in time slots view
  useEffect(() => {
    const fetchBookedSlots = async () => {
      if (!selectedShopForTimeSlots) return;
      
      try {
        setIsLoadingShopSlots(true);
        const formattedDate = format(selectedDate, 'yyyy-MM-dd');
        const shopAppts = await appointmentsService.getShopAppointments(selectedShopForTimeSlots);
        
        // Filter to appointments for the selected date
        const dateAppointments = shopAppts.filter(app => {
          if (app.appointmentDateTime) {
            const appDate = shopWallClockDate(app.appointmentDateTime, tzOf(app.shopId));
            return format(appDate, 'yyyy-MM-dd') === formattedDate;
          } else {
            // Should not reach here in the updated system
            console.warn('Found appointment without appointmentDateTime', app.id);
            return false;
          }
        });
        
        setShopAppointments(dateAppointments);
        
        // Extract booked time slots using consistent formatting
        const booked = dateAppointments.map(app => {
          if (app.appointmentDateTime) {
            const appDate = shopWallClockDate(app.appointmentDateTime, tzOf(app.shopId));
            return format(appDate, 'HH:mm');
          } else {
            // Should not reach here in the updated system
            console.warn('Found appointment without appointmentDateTime', app.id);
            return '';
          }
        }).filter(time => time !== ''); // Remove any empty strings
        
        setShopBookedSlots(booked);
        
        // Get time slots based on shop settings if not already loaded
        if (availableTimeSlots.length === 0 && shopSettings?.timeSlotSize) {
          // Get the day of week for the selected date
          const dayOfWeek = format(selectedDate, 'EEEE').toUpperCase();
          
          // Check for business hours in the settings
          let startHour = BUSINESS_HOURS.start;
          let endHour = BUSINESS_HOURS.end;
          
          // If we have business hours and they're defined for the selected day, use them
          if (shopSettings.businessHours && shopSettings.businessHours[dayOfWeek as keyof typeof shopSettings.businessHours]) {
            const hours = shopSettings.businessHours[dayOfWeek as keyof typeof shopSettings.businessHours];
            if (hours && hours.open && hours.close) {
              // Parse hours from HH:MM format
              startHour = parseInt(hours.open.split(':')[0]);
              endHour = parseInt(hours.close.split(':')[0]);
              
              // If close time is something like 18:30, make sure we include the full hour
              if (hours.close.split(':')[1] !== '00') {
                endHour += 1;
              }
            }
          }
          
          console.log(`Using business hours for ${dayOfWeek}: ${startHour}:00 - ${endHour}:00`);
          setAvailableTimeSlots(generateTimeSlots(shopSettings.timeSlotSize, startHour, endHour));
        }
      } catch (error) {
        console.error('Failed to load shop appointments:', error);
        setShopBookedSlots([]);
      } finally {
        setIsLoadingShopSlots(false);
      }
    };
    
    fetchBookedSlots();
    // Depends on the whole settings object, not just timeSlotSize: the block
    // above also reads businessHours, and keying on one field meant a business
    // hours change never regenerated the slots.
  }, [selectedShopForTimeSlots, selectedDate, shopSettings, availableTimeSlots.length, tzOf]);

  // Fetch shop settings
  useEffect(() => {
    const fetchShopSettings = async () => {
      if (!selectedShopForTimeSlots) return;
      
      try {
        setLoadingSettings(true);
        const settings = await shopsService.getShopSettings(selectedShopForTimeSlots);
        console.log(`Loaded shop settings for ${selectedShopForTimeSlots}. Time slot size: ${settings.timeSlotSize} minutes, max bookings: ${settings.maxConcurrentBookings}`);
        setShopSettings(settings);
        
        // Generate time slots based on the shop's time slot size and business hours
        if (settings && settings.timeSlotSize) {
          // Get today's day of week for business hours lookup
          const dayOfWeek = format(new Date(), 'EEEE').toUpperCase();
          
          // Check for business hours in the settings
          let startHour = BUSINESS_HOURS.start;
          let endHour = BUSINESS_HOURS.end;
          
          // If we have business hours and they're defined for today, use them
          if (settings.businessHours && settings.businessHours[dayOfWeek as keyof typeof settings.businessHours]) {
            const hours = settings.businessHours[dayOfWeek as keyof typeof settings.businessHours];
            if (hours && hours.open && hours.close) {
              // Parse hours from HH:MM format
              startHour = parseInt(hours.open.split(':')[0]);
              endHour = parseInt(hours.close.split(':')[0]);
              
              // If close time is something like 18:30, make sure we include the full hour
              if (hours.close.split(':')[1] !== '00') {
                endHour += 1;
              }
            }
          }
          
          console.log(`Using business hours: ${startHour}:00 - ${endHour}:00`);
          const timeSlots = generateTimeSlots(settings.timeSlotSize, startHour, endHour);
          setAvailableTimeSlots(timeSlots);
        }
      } catch (error) {
        console.error('Failed to load shop settings:', error);
        setShopSettings(null);
        // Use default 30-minute slots if settings couldn't be loaded
        setAvailableTimeSlots(generateTimeSlots(30));
      } finally {
        setLoadingSettings(false);
      }
    };
    
    fetchShopSettings();
  }, [selectedShopForTimeSlots]);

  // Thin bindings over the shared, pure helpers in utils/shopAvailability —
  // the same logic the public booking page uses.
  const isHoliday = useCallback(
    (date: Date): boolean => isHolidayOn(date, shopSettings),
    [shopSettings]
  );

  const isClosedDay = useCallback(
    (date: Date): boolean => isClosedDayOn(date, shopSettings),
    [shopSettings]
  );

  const getSpecialHours = useCallback(
    (date: Date) => getSpecialHoursOn(date, shopSettings),
    [shopSettings]
  );

  const isDateAvailable = useCallback(
    (date: Date): boolean => isDateOpen(date, shopSettings),
    [shopSettings]
  );

  const findNextAvailableDate = useCallback(
    (startDate: Date, daysToCheck = 14): Date =>
      findNextOpenDate(startDate, shopSettings, daysToCheck),
    [shopSettings]
  );

  // Move off a closed day once the shop's settings arrive. Guarded by the
  // date comparison, which is what stops it looping on its own update.
  useEffect(() => {
    if (!shopSettings || !selectedShopForTimeSlots) return;

    setSelectedDate((current) => {
      const nextAvailable = findNextOpenDate(current, shopSettings);
      return format(nextAvailable, 'yyyy-MM-dd') === format(current, 'yyyy-MM-dd')
        ? current
        : nextAvailable;
    });
  }, [shopSettings, selectedShopForTimeSlots]);


  // Calculate booking counts for each time slot
  useEffect(() => {
    if (!shopAppointments.length) {
      setSlotBookingCounts({});
      return;
    }
    
    const counts: Record<string, number> = {};
    
    shopAppointments.forEach(app => {
      let timeSlot = '';
      
      if (app.appointmentDateTime) {
        // Parse the epoch timestamp to ensure consistent time format
        const appDate = shopWallClockDate(app.appointmentDateTime, tzOf(app.shopId));
        // Format it consistently as HH:MM
        timeSlot = format(appDate, 'HH:mm');
      } else if (app.appointmentTime) {
        // Take only the hour and minute for consistent comparison
        timeSlot = app.appointmentTime.substring(0, 5);
      } else {
        console.warn('Appointment has no time information:', app.id);
        return; // Skip this appointment
      }
      
      counts[timeSlot] = (counts[timeSlot] || 0) + 1;
      console.log(`Counting slot ${timeSlot} for appointment ${app.id}, count: ${counts[timeSlot]}`);
    });
    
    console.log('Final slot booking counts:', counts);
    setSlotBookingCounts(counts);
  }, [shopAppointments, tzOf]);

  const handleEditClick = async (appointment: Appointment) => {
    setSelectedAppointment(appointment);

    // Load tasks for this appointment
    try {
      const tasks = await tasksService.getTasksByAppointmentId(appointment.id);
      setAppointmentTasks(tasks);
    } catch (error) {
      console.error('Failed to load tasks:', error);
      setAppointmentTasks([]);
    }

    setDialogOpen(true);
  };

  const handleRescheduleClick = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    
    try {
      // Use appointmentDateTime if available, fall back to appointmentDate
      if (appointment.appointmentDateTime) {
        // Convert epoch timestamp to Date object
        const parsedDate = shopWallClockDate(appointment.appointmentDateTime, tzOf(appointment.shopId));
        const timeStr = format(parsedDate, 'HH:mm');
        
        // If the date is available, use it; otherwise find the next available date
        if (isDateAvailable(parsedDate)) {
          setSelectedDate(parsedDate);
        } else {
          setSelectedDate(findNextAvailableDate(new Date()));
        }
        
        setSelectedTimeSlot(timeStr);
      } else if (appointment.appointmentDate) {
        // Convert epoch timestamp to Date object
        const parsedDate = shopWallClockDate(appointment.appointmentDate, tzOf(appointment.shopId));

        // If the date is available, use it; otherwise find the next available date
        if (isDateAvailable(parsedDate)) {
          setSelectedDate(parsedDate);
        } else {
          setSelectedDate(findNextAvailableDate(new Date()));
        }

        // Set the selected time slot from the appointment
        if (appointment.appointmentTime) {
          setSelectedTimeSlot(appointment.appointmentTime.substring(0, 5));
        }
      } else {
        // Fallback to current date if no valid date found
        console.error('No valid date found for appointment');
        setSelectedDate(findNextAvailableDate(new Date()));
      }
    } catch (e) {
      console.error('Failed to parse appointment date:', e);
      setSelectedDate(findNextAvailableDate(new Date()));
    }
    
    // Load available time slots for the selected shop and date
    loadTimeSlots(appointment.shopId);
    setRescheduleDialogOpen(true);
  };

  // Add a function to load time slots specifically for reschedule
  const loadTimeSlots = async (shopId: string) => {
    if (!shopId) return;
    
    try {
      setIsLoadingTimeSlots(true);
      
      // Get shop settings
      const settings = await shopsService.getShopSettings(shopId);
      
      // Generate time slots based on the shop's time slot size and business hours
      if (settings && settings.timeSlotSize) {
        // Get today's day of week for business hours lookup
        const dayOfWeek = format(selectedDate, 'EEEE').toUpperCase();
        
        // Check for business hours in the settings
        let startHour = BUSINESS_HOURS.start;
        let endHour = BUSINESS_HOURS.end;
        
        // If we have business hours and they're defined for today, use them
        if (settings.businessHours && settings.businessHours[dayOfWeek as keyof typeof settings.businessHours]) {
          const hours = settings.businessHours[dayOfWeek as keyof typeof settings.businessHours];
          if (hours && hours.open && hours.close) {
            // Parse hours from HH:MM format
            startHour = parseInt(hours.open.split(':')[0]);
            endHour = parseInt(hours.close.split(':')[0]);
            
            // If close time is something like 18:30, make sure we include the full hour
            if (hours.close.split(':')[1] !== '00') {
              endHour += 1;
            }
          }
        }
        
        console.log(`Using business hours: ${startHour}:00 - ${endHour}:00 for ${format(selectedDate, 'EEEE')}`);
        const timeSlots = generateTimeSlots(settings.timeSlotSize, startHour, endHour);
        setAvailableTimeSlots(timeSlots);
      } else {
        // Default to 30-minute slots if no settings available
        setAvailableTimeSlots(generateTimeSlots(30));
      }
      
      // Get appointments for this shop and date to determine booked slots
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      const shopAppts = await appointmentsService.getShopAppointments(shopId);
      
      // Filter appointments for the selected date
      const dateAppointments = shopAppts.filter(app => {
        if (app.appointmentDateTime) {
          // appointmentDateTime is now an epoch timestamp
          const appDate = shopWallClockDate(app.appointmentDateTime, tzOf(app.shopId));
          return format(appDate, 'yyyy-MM-dd') === formattedDate &&
                 (selectedAppointment ? app.id !== selectedAppointment.id : true);
        } else if (app.appointmentDate) {
          // appointmentDate is now an epoch timestamp
          const appDate = shopWallClockDate(app.appointmentDate, tzOf(app.shopId));
          const appDateStr = format(appDate, 'yyyy-MM-dd');
          return appDateStr === formattedDate &&
                 (selectedAppointment ? app.id !== selectedAppointment.id : true);
        }
        return false;
      });
      
      // Set the shop appointments for booking counts
      setShopAppointments(dateAppointments);
      
      // Extract booked time slots with consistent formatting
      const booked = dateAppointments.map(app => {
        if (app.appointmentDateTime) {
          const appDate = shopWallClockDate(app.appointmentDateTime, tzOf(app.shopId));
          return format(appDate, 'HH:mm');
        } else if (app.appointmentTime) {
          return app.appointmentTime.substring(0, 5);
        }
        return '';
      }).filter(Boolean); // Remove empty strings
      
      setShopBookedSlots(booked);
      
      // Calculate slot booking counts with consistent formatting
      const counts: Record<string, number> = {};
      dateAppointments.forEach(app => {
        let timeSlot = '';
        
        if (app.appointmentDateTime) {
          const appDate = shopWallClockDate(app.appointmentDateTime, tzOf(app.shopId));
          timeSlot = format(appDate, 'HH:mm');
        } else if (app.appointmentTime) {
          timeSlot = app.appointmentTime.substring(0, 5);
        } else {
          return; // Skip this appointment
        }
        
        counts[timeSlot] = (counts[timeSlot] || 0) + 1;
      });
      
      setSlotBookingCounts(counts);
    } catch (error) {
      console.error('Failed to load time slots:', error);
      setAvailableTimeSlots(generateTimeSlots(30));
    } finally {
      setIsLoadingTimeSlots(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedAppointment(null);
  };

  const handleRescheduleDialogClose = () => {
    setRescheduleDialogOpen(false);
    setSelectedTimeSlot('');
    setSelectedAppointment(null);
    setTimeSlotError(null);
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    if (selectedAppointment) {
      setSelectedAppointment({
        ...selectedAppointment,
        status: event.target.value as AppointmentStatus,
      });
    }
  };

  const handleMechanicChange = (event: SelectChangeEvent) => {
    if (selectedAppointment) {
      setSelectedAppointment({
        ...selectedAppointment,
        mechanicId: event.target.value,
      });
    }
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
      // Clear time slot when date changes
      setSelectedTimeSlot('');
      setTimeSlotError(null);
      
      // If we have a selected appointment, load time slots for the new date
      if (selectedAppointment) {
        loadTimeSlots(selectedAppointment.shopId);
      }
    }
  };

  const handleTimeSlotSelect = (timeSlot: string) => {
    const bookingCount = slotBookingCounts[timeSlot] || 0;
    const maxBookings = shopSettings?.maxConcurrentBookings || 1;
    const isFullyBooked = bookingCount >= maxBookings;
    const isCurrentAppointmentTime = selectedAppointment &&
      selectedAppointment.appointmentTime.substring(0, 5) === timeSlot &&
      selectedAppointment.appointmentDate &&
      format(selectedDate, 'yyyy-MM-dd') === format(shopWallClockDate(selectedAppointment.appointmentDate, tzOf(selectedAppointment.shopId)), 'yyyy-MM-dd');

    setTimeSlotError(null);

    if (isFullyBooked && !isCurrentAppointmentTime) {
      setTimeSlotError(`This time slot (${formatTimeForDisplay(timeSlot)}) is fully booked. Please select another time.`);
      return;
    }

    setSelectedTimeSlot(timeSlot);
  };

  const handleSaveChanges = async () => {
    if (!selectedAppointment) return;

    try {
      const original = appointments.find((app) => app.id === selectedAppointment.id);
      const updateData: UpdateAppointmentDto = {
        status: selectedAppointment.status,
        mechanicId: selectedAppointment.mechanicId,
        licensePlate: selectedAppointment.licensePlate || null,
        // Sent only when it changed: re-sending a bay the shop has since
        // removed would refuse an unrelated edit.
        ...((selectedAppointment.bay ?? null) !== (original?.bay ?? null)
          ? { bay: selectedAppointment.bay ?? null }
          : {}),
      };

      const updated = await appointmentsService.updateAppointment(selectedAppointment.id, updateData);
      
      // Update the appointments array; the plate comes back normalised.
      setAppointments(prev =>
        prev.map(app =>
          app.id === selectedAppointment.id
            ? { ...selectedAppointment, licensePlate: updated.licensePlate ?? null, bay: updated.bay ?? null }
            : app
        )
      );
      
      setSuccessMessage('Appointment updated successfully');
      handleDialogClose();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to update appointment'));
    }
  };

  const handleSaveReschedule = async () => {
    if (!selectedAppointment || !selectedTimeSlot) return;

    try {
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      const timeWithSeconds = selectedTimeSlot.length === 5 
        ? `${selectedTimeSlot}:00` 
        : selectedTimeSlot;

      // Use correct logic for fully booked
      const bookingCount = slotBookingCounts[selectedTimeSlot] || 0;
      const maxBookings = shopSettings?.maxConcurrentBookings || 1;
      const isFullyBooked = bookingCount >= maxBookings;
      
      // Check if it's the same date/time as current appointment
      let isSameDateTime = false;
      if (selectedAppointment.appointmentDateTime) {
        const appDate = shopWallClockDate(selectedAppointment.appointmentDateTime, tzOf(selectedAppointment.shopId));
        const currentDate = format(appDate, 'yyyy-MM-dd');
        const currentTime = format(appDate, 'HH:mm');
        isSameDateTime = currentDate === formattedDate && currentTime === selectedTimeSlot;
      } else if (selectedAppointment.appointmentDate && selectedAppointment.appointmentTime) {
        const appDate = shopWallClockDate(selectedAppointment.appointmentDate, tzOf(selectedAppointment.shopId));
        const currentDate = format(appDate, 'yyyy-MM-dd');
        isSameDateTime = currentDate === formattedDate &&
                        selectedAppointment.appointmentTime.substring(0, 5) === selectedTimeSlot;
      }
      
      if (isFullyBooked && !isSameDateTime) {
        setTimeSlotError(`Cannot reschedule to time ${formatTimeForDisplay(selectedTimeSlot)} as it is fully booked.`);
        return;
      }

      await appointmentsService.rescheduleAppointment(
        selectedAppointment.id,
        formattedDate,
        timeWithSeconds,
        shopSettings?.timezone
      );
      
      // Convert to epoch timestamps for state update
      const appointmentDateTimeObj = new Date(`${formattedDate} ${timeWithSeconds}`);
      const newAppointmentDateTime = Math.floor(appointmentDateTimeObj.getTime() / 1000);
      const newAppointmentDate = Math.floor(new Date(formattedDate).getTime() / 1000);

      setAppointments(prev =>
        prev.map(app => {
          if (app.id === selectedAppointment.id) {
            return {
              ...app,
              appointmentDate: newAppointmentDate,
              appointmentTime: timeWithSeconds,
              appointmentDateTime: newAppointmentDateTime
            };
          }
          return app;
        })
      );
      
      // Update the booking counts for the UI to reflect the change immediately
      if (!isSameDateTime) {
        setSlotBookingCounts(prev => {
          const newCounts = { ...prev };
          
          // Remove count from previous time slot if it exists
          if (selectedAppointment.appointmentDateTime) {
            const appDate = shopWallClockDate(selectedAppointment.appointmentDateTime, tzOf(selectedAppointment.shopId));
            const oldTimeSlot = format(appDate, 'HH:mm');
            if (newCounts[oldTimeSlot] && newCounts[oldTimeSlot] > 0) {
              newCounts[oldTimeSlot]--;
            }
          } else if (selectedAppointment.appointmentTime) {
            const oldTimeSlot = selectedAppointment.appointmentTime.substring(0, 5);
            if (newCounts[oldTimeSlot] && newCounts[oldTimeSlot] > 0) {
              newCounts[oldTimeSlot]--;
            }
          }
          
          // Add count to new time slot
          newCounts[selectedTimeSlot] = (newCounts[selectedTimeSlot] || 0) + 1;
          
          return newCounts;
        });
      }
      
      setSuccessMessage('Appointment rescheduled successfully');
      handleRescheduleDialogClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reschedule appointment');
    }
  };

  const handleGenerateInvoice = async (appointment: Appointment) => {
    setGeneratingInvoiceId(appointment.id);
    try {
      setError(null);
      await invoicesService.generateInvoice(appointment.id);
      setSuccessMessage(
        'Draft invoice created from this job’s tasks and moved to Ready for Pickup. Price it on the Invoices page.',
      );
      await refreshAppointments();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate invoice');
    } finally {
      setGeneratingInvoiceId(null);
    }
  };

  const handleDeleteAppointment = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) return;

    try {
      await appointmentsService.deleteAppointment(id);
      setAppointments(prev => prev.filter(app => app.id !== id));
      setSuccessMessage('Appointment deleted successfully');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete appointment');
    }
  };

  // Task Management Handlers
  const handleOpenTaskForm = (task?: Task) => {
    if (task) {
      setEditingTask(task);
      setTaskForm({
        title: task.title,
        description: task.description || '',
        status: task.status,
        assignedToId: task.assignedToId || '',
      });
    } else {
      setEditingTask(null);
      setTaskForm({
        title: '',
        description: '',
        status: TaskStatus.PENDING,
        assignedToId: '',
      });
    }
    setTaskFormDialogOpen(true);
  };

  const handleSaveTask = async () => {
    if (!selectedAppointment || !taskForm.title.trim()) {
      setError('Task title is required');
      return;
    }

    try {
      setError(null);
      if (editingTask) {
        // Update existing task
        const updateData: UpdateTaskDto = {
          title: taskForm.title,
          description: taskForm.description || undefined,
          status: taskForm.status,
          assignedToId: taskForm.assignedToId || undefined,
        };
        await tasksService.updateTask(editingTask.id, updateData);
        setSuccessMessage('Task updated successfully');
      } else {
        // Create new task
        const createData: CreateTaskDto = {
          title: taskForm.title,
          description: taskForm.description || undefined,
          status: taskForm.status,
          appointmentId: selectedAppointment.id,
          assignedToId: taskForm.assignedToId || undefined,
        };
        await tasksService.createTask(createData);
        setSuccessMessage('Task created successfully');
      }

      // Reload tasks
      const tasks = await tasksService.getTasksByAppointmentId(selectedAppointment.id);
      setAppointmentTasks(tasks);

      setTaskFormDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${editingTask ? 'update' : 'create'} task`);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      setError(null);
      await tasksService.deleteTask(taskId);
      setSuccessMessage('Task deleted successfully');

      // Reload tasks
      if (selectedAppointment) {
        const tasks = await tasksService.getTasksByAppointmentId(selectedAppointment.id);
        setAppointmentTasks(tasks);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete task');
    }
  };

  const handleUpdateTaskStatus = async (taskId: string, newStatus: TaskStatus) => {
    try {
      setError(null);
      await tasksService.updateTaskStatus(taskId, newStatus);
      setSuccessMessage('Task status updated successfully');

      // Reload tasks
      if (selectedAppointment) {
        const tasks = await tasksService.getTasksByAppointmentId(selectedAppointment.id);
        setAppointmentTasks(tasks);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update task status');
    }
  };

  const handleNotifyTask = async (taskId: string) => {
    try {
      setError(null);
      await tasksService.notifyCustomer(taskId);
      setSuccessMessage('Customer notified about the task');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to notify customer');
    }
  };

  const formatAppointmentDateTime = (appointment: Appointment) => {
    try {
      // Use appointmentDateTime, which should always be available now
      if (appointment.appointmentDateTime) {
        // Parse epoch timestamp
        const appDate = shopWallClockDate(appointment.appointmentDateTime, tzOf(appointment.shopId));
        
        // Format the date using the shop timezone if available
        const tzLabel = shopSettings?.timezone 
          ? timezoneOptions.find(tz => tz.value === shopSettings.timezone)?.label || '' 
          : '';
        
        return `${format(appDate, 'MMM d, yyyy h:mm a')}${tzLabel ? ` (${tzLabel})` : ''}`;
      } 
      
      // Should not reach here after migration
      console.warn('Found appointment without appointmentDateTime', appointment.id);
      return 'Invalid date/time';
    } catch (e) {
      console.error('Error formatting appointment date/time:', e);
      return 'Invalid date/time';
    }
  };

  // Determine a slot's status: available, partial (some bookings but not at limit), or full
  const getSlotStatus = (timeSlot: string) => {
    const maxBookings = shopSettings?.maxConcurrentBookings || 1;
    const currentBookings = slotBookingCounts[timeSlot] || 0;
    
    if (currentBookings === 0) {
      return 'available'; // Available - no bookings
    } else if (currentBookings < maxBookings) {
      return 'partial'; // Partial - has bookings but not at limit
    } else {
      return 'full'; // Full - at or over the booking limit
    }
  };

  // Handle time slot click with concurrent booking limit logic
  // Handle new booking time slot select
  const handleNewBookingTimeSlotSelect = (timeSlot: string) => {
    const status = getSlotStatus(timeSlot);
    if (status === 'full') {
      setNewBookingError(`This time slot (${formatTimeForDisplay(timeSlot)}) is fully booked. Please select another time.`);
      return;
    }
    
    setNewBookingTimeSlot(timeSlot);
    setNewBookingError(null);
  };
  
  /** Opens a blank booking, at `timeSlot` when staff clicked one. */
  const handleOpenNewBookingDialog = (timeSlot = '') => {
    setNewBookingForm({
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      vehicleModel: '',
      vehicleYear: '',
      licensePlate: '',
      serviceDescription: '',
      comments: '',
      mechanicId: ''
    });
    setNewBookingTimeSlot(timeSlot);
    setNewBookingError(null);
    setNewBookingDialogOpen(true);
  };
  
  // Handle form input changes
  const handleNewBookingFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewBookingForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle mechanic selection for new booking
  const handleNewBookingMechanicChange = (e: SelectChangeEvent) => {
    setNewBookingForm(prev => ({
      ...prev,
      mechanicId: e.target.value
    }));
  };
  
  // Create new booking
  const handleCreateNewBooking = async () => {
    if (!selectedShopForTimeSlots || !newBookingTimeSlot || !selectedDate) {
      setNewBookingError('Please select a shop, date, and time slot first.');
      return;
    }
    
    setSavingBooking(true);
    setNewBookingError(null);
    try {
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');

      // The slot is a shop-local time. Building the instant with `new Date(...)`
      // reads it in the browser's zone, which files the wrong hour whenever the
      // shop and the person at the desk are not in the same zone.
      const appointmentDateTime = shopWallClockToEpoch(
        formattedDate,
        newBookingTimeSlot,
        shopSettings?.timezone
      );

      const newBookingData: CreateAppointmentDto = {
        shopId: selectedShopForTimeSlots,
        appointmentDateTime,
        customerName: newBookingForm.customerName,
        customerEmail: newBookingForm.customerEmail,
        customerPhone: newBookingForm.customerPhone,
        vehicleModel: newBookingForm.vehicleModel,
        vehicleYear: newBookingForm.vehicleYear,
        licensePlate: newBookingForm.licensePlate || null,
        serviceDescription: newBookingForm.serviceDescription,
        comments: newBookingForm.comments,
        // Both are honoured because this request carries a staff token; the
        // same fields from the public form are ignored server-side.
        ...(newBookingForm.mechanicId ? { mechanicId: newBookingForm.mechanicId } : {}),
        status: AppointmentStatus.CONFIRMED,
      };
      
      const response = await appointmentsService.createAppointment(newBookingData);
      
      // Add the new appointment to the list
      setAppointments(prev => [...prev, response]);
      
      // Refresh the shop appointments
      if (selectedShopForTimeSlots) {
        const shopAppts = await appointmentsService.getShopAppointments(selectedShopForTimeSlots);
        const dateAppointments = shopAppts.filter(app => {
          if (app.appointmentDateTime) {
            const appDate = shopWallClockDate(app.appointmentDateTime, tzOf(app.shopId));
            return format(appDate, 'yyyy-MM-dd') === formattedDate;
          } else if (app.appointmentDate) {
            const appDate = shopWallClockDate(app.appointmentDate, tzOf(app.shopId));
            return format(appDate, 'yyyy-MM-dd') === formattedDate;
          }
          return false;
        });
        setShopAppointments(dateAppointments);
      }
      
      setSuccessMessage('Booking created successfully!');
      setNewBookingDialogOpen(false);
    } catch (err: unknown) {
      // Shown in the dialog: the page's own alert sits behind it.
      setNewBookingError(apiErrorMessage(err, 'Failed to create booking'));
    } finally {
      setSavingBooking(false);
    }
  };

  const renderAppointmentsListFor = (list: Appointment[]) => (
    <AppointmentsTable
      list={list}
      loading={loading}
      formatAppointmentDateTime={formatAppointmentDateTime}
      generatingInvoiceId={generatingInvoiceId}
      handleEditClick={handleEditClick}
      handleRescheduleClick={handleRescheduleClick}
      handleDeleteAppointment={handleDeleteAppointment}
      handleGenerateInvoice={handleGenerateInvoice}
    />
  );

  const todayAppointments = useMemo(() => {
    const today = shopNow();
    return appointments.filter((appointment) => {
      const date = getAppointmentDate(appointment, tzOf(appointment.shopId));
      return date ? isSameDay(date, today) : false;
    });
  }, [appointments, shopNow, tzOf]);

  const weekAppointments = useMemo(() => {
    const today = shopNow();
    const start = startOfWeek(today);
    const end = endOfWeek(today);
    return appointments.filter((appointment) => {
      const date = getAppointmentDate(appointment, tzOf(appointment.shopId));
      return date ? date >= start && date <= end : false;
    });
  }, [appointments, shopNow, tzOf]);

  const appointmentsByStatus = useMemo(() => {
    return appointments.reduce<Record<string, Appointment[]>>((acc, appointment) => {
      const status = appointment.status;
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(appointment);
      return acc;
    }, {});
  }, [appointments]);

  const weekData = useMemo(() => {
    const data: Record<string, { start: Date; count: number }> = {};
    appointments.forEach((appointment) => {
      const date = getAppointmentDate(appointment, tzOf(appointment.shopId));
      if (!date) return;
      const start = startOfWeek(date);
      const key = format(start, 'yyyy-MM-dd');
      if (!data[key]) {
        data[key] = { start, count: 0 };
      }
      data[key].count += 1;
    });
    return data;
  }, [appointments, tzOf]);

  const weeklyStats = useMemo(() => {
    const makeLabel = (start: Date) => {
      const end = addDays(start, 6);
      return `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`;
    };

    const today = shopNow();
    const currentStart = startOfWeek(today);
    const nextStart = addDays(currentStart, 7);
    const currentKey = format(currentStart, 'yyyy-MM-dd');
    const nextKey = format(nextStart, 'yyyy-MM-dd');

    const stats = [
      {
        label: `Current Week (${makeLabel(currentStart)})`,
        count: weekData[currentKey]?.count || 0,
      },
      {
        label: `Next Week (${makeLabel(nextStart)})`,
        count: weekData[nextKey]?.count || 0,
      },
    ];

    const extras = Object.entries(weekData)
      .filter(([key]) => key !== currentKey && key !== nextKey)
      .map(([_, value]) => value)
      .sort((a, b) => b.start.getTime() - a.start.getTime());

    extras.forEach((value) => {
      stats.push({
        label: makeLabel(value.start),
        count: value.count,
      });
    });

    return stats;
  }, [weekData, shopNow]);

  const renderAppointmentsList = (list?: Appointment[]) => {
    return renderAppointmentsListFor(list ?? appointments);
  };

  const renderAppointmentCategories = () => (
    <AppointmentCategories
      appointments={appointments}
      appointmentsByStatus={appointmentsByStatus}
      weeklyStats={weeklyStats}
      formatAppointmentDateTime={formatAppointmentDateTime}
      handleEditClick={handleEditClick}
      handleRescheduleClick={handleRescheduleClick}
    />
  );

  const renderTimeSlots = () => (
    <TimeSlotsTab
      shopIds={timeSlotShopIds}
      shops={shops}
      availableTimeSlots={availableTimeSlots}
      slotBookingCounts={slotBookingCounts}
      shopSettings={shopSettings}
      selectedDate={selectedDate}
      selectedShopForTimeSlots={selectedShopForTimeSlots}
      setSelectedShopForTimeSlots={setSelectedShopForTimeSlots}
      shopAppointments={shopAppointments}
      shopBookedSlots={shopBookedSlots}
      isLoadingShopSlots={isLoadingShopSlots}
      loadingSettings={loadingSettings}
      isDateAvailable={isDateAvailable}
      isHoliday={isHoliday}
      isClosedDay={isClosedDay}
      getSpecialHours={getSpecialHours}
      handleDateChange={handleDateChange}
      handleOpenNewBookingDialog={handleOpenNewBookingDialog}
      handleRescheduleClick={handleRescheduleClick}
      formatAppointmentDateTime={formatAppointmentDateTime}
    />
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
        <PageHeader
          title="Appointments"
          subtitle="Every booking for your shop, updated live as jobs progress."
        />
        <BlockSkeleton lines={10} />
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
        <PageHeader
          title="Appointments"
          subtitle="Every booking for your shop, updated live as jobs progress."
        />


        {loadError ? (
          <Alert severity="error" sx={{ mb: 4 }}>
            {apiErrorMessage(loadError, 'Failed to fetch data')}
          </Alert>
        ) : null}
        {error && (
          <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 4 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            mb: 3,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="appointment management tabs">
            <Tab label="All Appointments" />
            <Tab label="Available Time Slots" />
          </Tabs>

          {/* Bounds the query. "All time" is still available, but is no longer
              what the page does by default. */}
          <TextField
            select
            size="small"
            label="Showing"
            value={historyWindow}
            onChange={(event) => setHistoryWindow(event.target.value as HistoryWindow)}
            sx={{ minWidth: 180, mb: 1 }}
          >
            {HISTORY_WINDOWS.map((entry) => (
              <MenuItem key={entry.key} value={entry.key}>
                {entry.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={appointmentListTab} onChange={handleAppointmentListTabChange} aria-label="appointment view tabs">
              <Tab label="All" />
              <Tab label="Today" />
              <Tab label="Week" />
              <Tab label="Categories" />
            </Tabs>
          </Box>
          {appointmentListTab === 0 && renderAppointmentsList()}
          {appointmentListTab === 1 && renderAppointmentsList(todayAppointments)}
          {appointmentListTab === 2 && renderAppointmentsList(weekAppointments)}
          {appointmentListTab === 3 && renderAppointmentCategories()}
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          {renderTimeSlots()}
        </TabPanel>
        
        <EditAppointmentDialog
          dialogOpen={dialogOpen}
          selectedAppointment={selectedAppointment}
          appointmentTasks={appointmentTasks}
          mechanics={mechanics}
          formatAppointmentDateTime={formatAppointmentDateTime}
          handleDialogClose={handleDialogClose}
          handleStatusChange={handleStatusChange}
          handleMechanicChange={handleMechanicChange}
          handleDetailsChange={(patch) =>
            setSelectedAppointment((current) => (current ? { ...current, ...patch } : current))
          }
          bayCount={shopSettings?.bayCount ?? 0}
          handleNotifyTask={handleNotifyTask}
          handleSaveChanges={handleSaveChanges}
          setTaskDialogOpen={setTaskDialogOpen}
        />

        <RescheduleDialog
          rescheduleDialogOpen={rescheduleDialogOpen}
          selectedAppointment={selectedAppointment}
          selectedDate={selectedDate}
          selectedTimeSlot={selectedTimeSlot}
          availableTimeSlots={availableTimeSlots}
          isLoadingTimeSlots={isLoadingTimeSlots}
          slotBookingCounts={slotBookingCounts}
          shopSettings={shopSettings}
          timeSlotError={timeSlotError}
          setTimeSlotError={setTimeSlotError}
          isDateAvailable={isDateAvailable}
          tzOf={tzOf}
          formatAppointmentDateTime={formatAppointmentDateTime}
          handleDateChange={handleDateChange}
          handleTimeSlotSelect={handleTimeSlotSelect}
          handleRescheduleDialogClose={handleRescheduleDialogClose}
          handleSaveReschedule={handleSaveReschedule}
        />

        <NewBookingDialog
          newBookingDialogOpen={newBookingDialogOpen}
          setNewBookingDialogOpen={setNewBookingDialogOpen}
          shopName={shops.find((shop) => shop.id === selectedShopForTimeSlots)?.name}
          selectedDate={selectedDate}
          availableTimeSlots={availableTimeSlots}
          slotBookingCounts={slotBookingCounts}
          maxBookings={shopSettings?.maxConcurrentBookings || 1}
          newBookingTimeSlot={newBookingTimeSlot}
          handleNewBookingTimeSlotSelect={handleNewBookingTimeSlotSelect}
          newBookingForm={newBookingForm}
          handleNewBookingFormChange={handleNewBookingFormChange}
          handleNewBookingMechanicChange={handleNewBookingMechanicChange}
          mechanics={mechanics}
          selectedShopForTimeSlots={selectedShopForTimeSlots}
          error={newBookingError}
          saving={savingBooking}
          handleCreateNewBooking={handleCreateNewBooking}
        />

        <TaskManagerDialog
          taskDialogOpen={taskDialogOpen}
          setTaskDialogOpen={setTaskDialogOpen}
          selectedAppointment={selectedAppointment}
          appointmentTasks={appointmentTasks}
          formatAppointmentDateTime={formatAppointmentDateTime}
          handleOpenTaskForm={handleOpenTaskForm}
          handleUpdateTaskStatus={handleUpdateTaskStatus}
          handleDeleteTask={handleDeleteTask}
        />

        <TaskFormDialog
          taskFormDialogOpen={taskFormDialogOpen}
          setTaskFormDialogOpen={setTaskFormDialogOpen}
          editingTask={editingTask}
          taskForm={taskForm}
          setTaskForm={setTaskForm}
          selectedAppointment={selectedAppointment}
          mechanics={mechanics}
          handleSaveTask={handleSaveTask}
        />
      </Container>
    </LocalizationProvider>
  );
};

export default AppointmentsPage; 